import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Center,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  ModalBody,
  ModalHeader,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import type { SourcingEventTemplateDto } from "../../../../../../autogen/bff-api";
import { SourceBadge } from "../../../../../../common/library/SourceBadge";
import { useProcurementTerms } from "../../../../../../common/terminology/useProcurementTerms";
import { templateSummary } from "../../../../../../common/library/templateSummary";
import { useRunsPublicProcurements } from "../../../../../../common/organization/useRunsPublicProcurements";

// t('Apply procurement template') t('No templates yet') t('No templates match the search')
// t('Search among {{count}} templates')
// t('A template fills in qualification requirements, award criteria and minimum requirements. You see exactly what is added before you confirm, and can always edit afterwards. Only the section you are in is preselected — you can bring in more in the next step.')

interface Props {
  templates: SourcingEventTemplateDto[];
  isLoading: boolean;
  searchRef: RefObject<HTMLInputElement>;
  onPick: (template: SourcingEventTemplateDto) => void;
}

export const TemplatePickerStep = ({ templates, isLoading, searchRef, onPick }: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const runsPublicProcurements = useRunsPublicProcurements();
  const [query, setQuery] = useState("");

  const needle = query.trim().toLowerCase();
  const matches = needle
    ? templates.filter((tmpl) =>
        [tmpl.name, tmpl.category, tmpl.description].some((field) =>
          (field ?? "").toLowerCase().includes(needle)
        )
      )
    : templates;

  return (
    <>
      <ModalHeader pb="4">
        <Text>{t("Apply procurement template")}</Text>
        <Text fontSize="sm" fontWeight="normal" color="smMuted" mt="2">
          {t(
            "A template fills in qualification requirements, award criteria and minimum requirements. You see exactly what is added before you confirm, and can always edit afterwards. Only the section you are in is preselected — you can bring in more in the next step."
          )}
        </Text>
      </ModalHeader>
      {/* scrollBehavior="inside" makes this the scroller, and Chrome gives a scroller its own
          tab stop and focus ring. The search field and the cards are reachable without it. */}
      <ModalBody pb="6" borderTopWidth="1px" borderColor="smBorder" pt="5" tabIndex={-1}>
        {isLoading ? (
          <Center py="10">
            <Spinner color="smPrimary" />
          </Center>
        ) : templates.length === 0 ? (
          <Text color="smMuted" py="6" textAlign="center">
            {t("No templates yet")}
          </Text>
        ) : (
          <VStack align="stretch" spacing="3">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="smMuted" />
              </InputLeftElement>
              <Input
                ref={searchRef}
                autoComplete="off"
                data-1p-ignore
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("Search among {{count}} templates", { count: templates.length }) ?? ""}
              />
            </InputGroup>
            {matches.length === 0 ? (
              <Text color="smMuted" py="6" textAlign="center">
                {t("No templates match the search")}
              </Text>
            ) : (
              matches.map((tmpl) => (
                <Box
                  key={tmpl.id}
                  as="button"
                  type="button"
                  textAlign="left"
                  borderWidth="1px"
                  borderColor="smBorder"
                  borderRadius="lg"
                  p="4"
                  onClick={() => onPick(tmpl)}
                  _hover={{ borderColor: "smPrimary", bg: "smBackgroundSecondary" }}
                >
                  <Flex align="center" gap="3" flexWrap="wrap">
                    <Text fontWeight="bold">{tmpl.name}</Text>
                    <SourceBadge global={tmpl.owner.global} />
                  </Flex>
                  {(tmpl.description || tmpl.category) && (
                    <Text color="smMuted" fontSize="sm" mt="1">
                      {[tmpl.category, tmpl.description].filter(Boolean).join(" — ")}
                    </Text>
                  )}
                  <Text color="smMuted" fontSize="sm" mt="1">
                    {templateSummary(tmpl.content, t, terms, runsPublicProcurements)}
                  </Text>
                </Box>
              ))
            )}
          </VStack>
        )}
      </ModalBody>
    </>
  );
};
